import { createSelector } from 'reselect'
import * as R from 'ramda'
import { getP, PType } from 'redux-polyglot'
import {
  isTypeNavigation,
  isTypeProduct
} from 'domain/resources'
import {
  ALL_PRODUCTS_SCOPE_URI,
  ALL_PRODUCTS_TYPE
} from 'constants/navigation'
import { toHref } from 'helpers/PathHelper'
import { ascendingSortNumbersArray } from 'helpers/sortNumbers'
import { getResources } from 'store/reducers/resources' /* this selector needs to be relocated */
import {
  AllCitiesAndDeparturesDatesAndDurationsOfUniverses,
  GroupedDurations,
  Resource,
  Resources,
  UniverseResource
} from 'types/typescript'
import {
  Diff,
  FillCitiesAndDeparturesDatesAndDurationsAcc,
  FillGroupedDurationsAcc,
  GetAllCitiesAndDeparturesDatesAndDurationsOfUniverses,
  GetAllCitiesOfUniverses,
  GetAllDeparturesDatesOfUniverses,
  GetAllDurationsOfUniverses,
  GetAllGroupedDurationsOfUniverses,
  GetAllProductsResource,
  GetAllProductsUrl,
  GetFirstChild,
  GetSeeAllProductsPathname,
  OrderGroupedDurations
} from './allProducts.types'
import { getOperationCode } from './application'
import { getQuery } from './router'
import { getSaleResource, getSaleSlug } from './sale'

export const getSeeAllProductsPathname: GetSeeAllProductsPathname = createSelector(
  getOperationCode,
  getSaleSlug,
  (opeCode, saleSlug) => `/${opeCode}/${saleSlug}/${ALL_PRODUCTS_SCOPE_URI}`
)

const getFirstChild: GetFirstChild = (resources, currentResource) => resources[R.head(currentResource.resources)]

const fillCitiesAndDeparturesDatesAndDurationsAcc: FillCitiesAndDeparturesDatesAndDurationsAcc = (
  acc,
  ecomm,
  key
) => {
  if (Array.isArray(ecomm[key])) {
    if (!acc[key]) {
      acc[key] = [] as string[] & number[]
    }
    acc[key]?.push(...ecomm[key])
  }
}

const diff: Diff = (a, b) => a - b

const orderGroupedDurations: OrderGroupedDurations = (range1, range2) => {
  if (range1.minimum > range2.minimum) return 1
  if (range2.minimum > range1.minimum) return -1

  return 0
}

const fillGroupedDurationsAcc: FillGroupedDurationsAcc = (acc, ecomm, key) => {
  const groupedDurations = ecomm[key]

  if (!acc[key]) {
    acc[key] = []
  }

  const allGroupedDurations: GroupedDurations[] = Array.isArray(groupedDurations)
    ? groupedDurations.reduce(
      (allDurations, duration) => {
        const index = allDurations.findIndex((d: GroupedDurations): boolean => d.minimum === duration.minimum)

        if (index !== -1) {
          const values = [...duration.values, ...allDurations[index].values]
          const sortedValues = ascendingSortNumbersArray([...new Set(values)])

          allDurations[index] = { ...duration, values: sortedValues }
        } else {
          allDurations.push(duration)
        }
        return allDurations
      },
      [...acc[key]]
    )
    : undefined

  acc[key] = allGroupedDurations
}

export const getAllCitiesAndDeparturesDatesAndDurationsOfUniverses: GetAllCitiesAndDeparturesDatesAndDurationsOfUniverses = createSelector(
  getResources,
  (resources) => {
    const res: AllCitiesAndDeparturesDatesAndDurationsOfUniverses = Object.values(resources).reduce((acc: AllCitiesAndDeparturesDatesAndDurationsOfUniverses, resource: Resource) => {
      const { ecomm } = resource

      if (isTypeNavigation(resource) && ecomm) {
        const firstChild: Resource | undefined = getFirstChild(resources, resource)

        if (firstChild && !isTypeNavigation(firstChild)) {
          fillCitiesAndDeparturesDatesAndDurationsAcc(acc, ecomm as UniverseResource['ecomm'], 'cities')
          fillCitiesAndDeparturesDatesAndDurationsAcc(acc, ecomm as UniverseResource['ecomm'], 'departuresDates')
          fillCitiesAndDeparturesDatesAndDurationsAcc(acc, ecomm as UniverseResource['ecomm'], 'durations')
          fillGroupedDurationsAcc(acc, ecomm as UniverseResource['ecomm'], 'groupedDurations')
        }
      }
      return acc
    }, {})

    if (res.cities) {
      res.cities = R.uniq(res.cities).sort()
    }

    if (res.departuresDates) {
      res.departuresDates = R.pipe(
        R.uniq,
        R.sort(diff)
      )(res.departuresDates)
    }

    if (res.durations) {
      res.durations = ascendingSortNumbersArray([...new Set(res.durations)])
    }

    if (res.groupedDurations) {
      res.groupedDurations = res.groupedDurations.sort(orderGroupedDurations)
    }
    return res
  }
)

export const getAllCitiesOfUniverses: GetAllCitiesOfUniverses = createSelector(
  getAllCitiesAndDeparturesDatesAndDurationsOfUniverses,
  R.pipe(
    R.prop('cities'),
    R.defaultTo([])
  )
)

export const getAllDeparturesDatesOfUniverses: GetAllDeparturesDatesOfUniverses = createSelector(
  getAllCitiesAndDeparturesDatesAndDurationsOfUniverses,
  R.pipe(
    R.prop('departuresDates'),
    R.defaultTo([])
  )
)

export const getAllDurationsOfUniverses: GetAllDurationsOfUniverses = createSelector(
  getAllCitiesAndDeparturesDatesAndDurationsOfUniverses,
  R.pipe(
    R.prop('durations'),
    R.defaultTo([])
  )
)

export const getAllGroupedDurationsOfUniverses: GetAllGroupedDurationsOfUniverses = createSelector(
  getAllCitiesAndDeparturesDatesAndDurationsOfUniverses,
  R.pipe(
    R.prop('groupedDurations'),
    R.defaultTo([])
  )
)

export const getAllProductsResource: GetAllProductsResource = createSelector(
  getSaleResource,
  getResources,
  getSeeAllProductsPathname,
  getP,
  getAllCitiesAndDeparturesDatesAndDurationsOfUniverses,
  (sale, resources, pathname, p: PType, citiesAndDatesAndDurations) => {
    if (!sale) return undefined

    type Getter = (resources: Resources) => boolean
    const saleKeyEquals = (getter: Getter): boolean => R.compose(R.equals(sale.key), getter)

    return {
      content: {
        title: p.tc('catalog.all_products')
      },
      ecomm: citiesAndDatesAndDurations,
      key: ALL_PRODUCTS_TYPE,
      linkTo: { pathname },
      parent: sale.key,
      resources: R.compose(
        R.values,
        R.pluck('key'),
        R.filter(R.both(isTypeProduct, saleKeyEquals(R.prop('sale'))))
      )(resources),
      sale: sale.key,
      slug: ALL_PRODUCTS_SCOPE_URI,
      type: ALL_PRODUCTS_TYPE
    }
  }
)

export const getAllProductsUrl: GetAllProductsUrl = createSelector(
  getAllProductsResource,
  getQuery,
  (allProducts, query) => {
    return toHref({
      pathname: allProducts?.linkTo.pathname
    }, query)
  }
)
